/**
 * Clarix — Supplier Profile Page
 * Route: /suppliers/:id
 *
 * Tabs:
 *   Overview      — contact details, stats, notes
 *   Documents     — all documents from this supplier across all projects
 *   Line Items    — extracted pricing data + price trend chart
 *   Ratings       — rating history + breakdown
 *   Offer History — every offer ever filed, with comparison context (Phase C)
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const V = {
  ink:      '#0c1830',
  ink2:     '#2a3956',
  ink3:     '#5a6680',
  ink4:     '#8a93a6',
  paper:    '#fbfaf6',
  paper2:   '#f3f1ea',
  paper3:   '#e6e2d5',
  line:     '#d8d3c2',
  gold:     '#c8a64b',
  goldSoft: '#fdf8ee',
  good:     '#2a6f4f',
  goodSoft: '#e8f0eb',
  bad:      '#9c2b1a',
  badSoft:  '#f4e8e5',
  warn:     '#a8731a',
  warnSoft: '#f5ecd9',
  serif:    "'Source Serif 4', Georgia, serif",
  sans:     "'Inter', -apple-system, system-ui, sans-serif",
  mono:     "'JetBrains Mono', ui-monospace, monospace",
};

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('clarix-fonts')) return;
    const link = document.createElement('link');
    link.id   = 'clarix-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel  = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── CATEGORY CONFIG ──────────────────────────────────────────────────────────

const CATEGORIES = {
  steel:      { label: 'Steel',      color: '#5b21b6', bg: '#ede9fe' },
  bunkers:    { label: 'Bunkers',    color: '#1a4d8f', bg: '#dbeafe' },
  electrical: { label: 'Electrical', color: '#a8731a', bg: '#f5ecd9' },
  civil:      { label: 'Civil',      color: '#2a6f4f', bg: '#e8f0eb' },
  equipment:  { label: 'Equipment',  color: '#7c2d12', bg: '#fef3c7' },
  services:   { label: 'Services',   color: '#374151', bg: '#f3f4f6' },
  other:      { label: 'Other',      color: '#5a6680', bg: '#e6e2d5' },
};

function CategoryBadge({ category }) {
  const cfg = CATEGORIES[category] || CATEGORIES.other;
  return (
    <span style={{ fontFamily: V.mono, fontSize: 10, fontWeight: 500, letterSpacing: 1, padding: '3px 10px', borderRadius: 3, textTransform: 'uppercase', color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function StarRating({ value, size = 14 }) {
  if (value == null) return <span style={{ fontSize: 12, color: V.ink4 }}>No ratings yet</span>;
  const stars = Math.round(value);
  return (
    <span style={{ fontFamily: V.mono, fontSize: size, color: V.gold }}>
      {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
      <span style={{ color: V.ink3, marginLeft: 8, fontSize: size - 2 }}>{value.toFixed(1)} / 5</span>
    </span>
  );
}

function formatValue(v, cur = 'EUR') {
  if (!v) return '—';
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym}${Number(v).toLocaleString('en-GB')}`;
}

// ─── DOC TYPE TAG ─────────────────────────────────────────────────────────────

const DOC_COLORS = {
  quotation: { color: '#1a4d8f', bg: '#dbeafe' },
  proposal:  { color: V.warn,    bg: V.warnSoft },
  contract:  { color: V.good,    bg: V.goodSoft },
  invoice:   { color: V.ink2,    bg: V.paper3 },
  amendment: { color: V.bad,     bg: V.badSoft },
  BOQ:       { color: '#1a4d8f', bg: '#dbeafe' },
  other:     { color: V.ink3,    bg: V.paper2 },
  Quotation: { color: '#1a4d8f', bg: '#dbeafe' },
  Proposal:  { color: V.warn,    bg: V.warnSoft },
  Contract:  { color: V.good,    bg: V.goodSoft },
  Invoice:   { color: V.ink2,    bg: V.paper3 },
  Amendment: { color: V.bad,     bg: V.badSoft },
};

function DocTypeBadge({ type }) {
  const cfg = DOC_COLORS[type] || DOC_COLORS.other;
  return (
    <span style={{ fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 1, padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase', color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
      {type || '—'}
    </span>
  );
}

// ─── RATE SUPPLIER MODAL ──────────────────────────────────────────────────────

function RateModal({ supplierId, onClose, onSaved }) {
  const [form, setForm]     = useState({ rating: 3, category: 'overall', comment: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await api.post(`/api/suppliers/${supplierId}/ratings`, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...s.modal, maxWidth: 400 }}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div style={s.modalTitle}>Rate Supplier</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...s.modalBody, gap: 14 }}>
          <div style={s.field}>
            <label style={s.fieldLabel}>Category</label>
            <select style={s.input} value={form.category} onChange={e => setF('category', e.target.value)}>
              {['overall', 'price', 'quality', 'delivery', 'responsiveness'].map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Rating</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setF('rating', n)}
                  style={{
                    width: 36, height: 36,
                    border: `1px solid ${n <= form.rating ? V.gold : V.line}`,
                    borderRadius: 4,
                    background: n <= form.rating ? '#fdf8ee' : V.paper,
                    fontSize: 18, cursor: 'pointer',
                    color: n <= form.rating ? V.gold : V.line,
                  }}
                >★</button>
              ))}
            </div>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Comment <span style={{ color: V.ink4, fontWeight: 400 }}>(optional)</span></label>
            <textarea style={{ ...s.input, resize: 'vertical', minHeight: 60 }} placeholder="e.g. Excellent delivery, pricing competitive…" value={form.comment} onChange={e => setF('comment', e.target.value)} />
          </div>
          {error && <div style={s.errorBox}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Submit rating'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TAB: OVERVIEW ────────────────────────────────────────────────────────────

function OverviewTab({ supplier, recentDocuments, onRate, onPreview }) {
  const kpis = [
    { label: 'DOCUMENTS',      value: supplier.document_count     || 0 },
    { label: 'LINE ITEMS',     value: supplier.line_item_count    || 0 },
    { label: 'VERIFICATIONS',  value: supplier.verification_count || 0 },
    { label: 'RATING',         value: supplier.rating != null ? supplier.rating.toFixed(1) : '—', accent: supplier.rating != null },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: V.line, border: `1px solid ${V.line}`, borderRadius: 4, overflow: 'hidden' }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ background: V.paper, padding: '18px 22px', borderTop: `2px solid ${k.accent ? V.gold : V.line}` }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginBottom: 8, textTransform: 'uppercase', fontFamily: V.sans }}>{k.label}</div>
            <div style={{ fontFamily: V.mono, fontSize: 28, fontWeight: 500, color: k.accent ? V.gold : V.ink }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={s.card}>
          <div style={s.cardTitle}>Contact Details</div>
          {[
            { label: 'Contact', value: supplier.contact_name },
            { label: 'Email',   value: supplier.contact_email },
            { label: 'Phone',   value: supplier.contact_phone },
            { label: 'Country', value: supplier.country },
            { label: 'Added',   value: supplier.created_at ? new Date(supplier.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null },
          ].map(row => row.value ? (
            <div key={row.label} style={{ display: 'flex', gap: 12, paddingTop: 10, borderTop: `0.5px solid ${V.line}` }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: V.ink4, width: 70, flexShrink: 0, fontFamily: V.sans, paddingTop: 1 }}>{row.label}</div>
              <div style={{ fontSize: 13, color: V.ink, fontFamily: V.sans }}>{row.value}</div>
            </div>
          ) : null)}
          {!supplier.contact_name && !supplier.contact_email && !supplier.contact_phone && (
            <div style={{ fontSize: 12, color: V.ink4, fontFamily: V.sans, paddingTop: 8 }}>No contact details on file.</div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={s.card}>
            <div style={s.cardTitle}>Internal Notes</div>
            {supplier.notes
              ? <div style={{ fontSize: 13, color: V.ink2, fontFamily: V.sans, lineHeight: 1.6 }}>{supplier.notes}</div>
              : <div style={{ fontSize: 12, color: V.ink4, fontFamily: V.sans }}>No notes yet.</div>
            }
          </div>
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={s.cardTitle}>Supplier Rating</div>
              <button style={s.btnGhost} onClick={onRate}>+ Rate</button>
            </div>
            <StarRating value={supplier.rating} size={16} />
          </div>
        </div>
      </div>

      {recentDocuments && recentDocuments.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>Recent Documents</div>
          <div>
            {recentDocuments.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `0.5px solid ${V.line}`, cursor: onPreview ? 'pointer' : 'default' }} onClick={() => onPreview && onPreview(doc.document_id || doc.id, doc.original_name)}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: V.ink, fontFamily: V.mono }}>{doc.original_name}</div>
                  <div style={{ fontSize: 11, color: V.ink4, marginTop: 2, fontFamily: V.sans }}>{doc.project_name || 'No project'} · {doc.doc_date || 'No date'}</div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <DocTypeBadge type={doc.doc_type} />
                  {doc.total_value && <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink2 }}>{formatValue(doc.total_value, doc.currency)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB: DOCUMENTS ───────────────────────────────────────────────────────────

function DocumentsTab({ supplierId, onPreview }) {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState('');

  useEffect(() => {
    setLoading(true);
    const p = docType ? `?doc_type=${docType}` : '';
    api.get(`/api/suppliers/${supplierId}/documents${p}`)
      .then(r => setDocs(r.data.documents || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [supplierId, docType]);

  if (loading) return <div style={{ fontSize: 13, color: V.ink4, padding: 24 }}>Loading documents…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <select style={s.filterSelect} value={docType} onChange={e => setDocType(e.target.value)}>
          <option value="">All types</option>
          {['quotation', 'proposal', 'contract', 'invoice', 'amendment', 'BOQ', 'other'].map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
      </div>

      {docs.length === 0 ? (
        <div style={{ fontSize: 13, color: V.ink4, padding: '24px 0', fontFamily: V.sans }}>No documents for this supplier yet.</div>
      ) : (
        <div style={{ border: `0.5px solid ${V.line}`, borderRadius: 6, background: V.paper, overflow: 'hidden' }}>
          {docs.map((doc, i) => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: i < docs.length - 1 ? `0.5px solid ${V.line}` : 'none', cursor: 'pointer' }} onClick={() => onPreview && onPreview(doc.document_id || doc.id, doc.original_name)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: V.ink, fontFamily: V.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_name}</div>
                <div style={{ fontSize: 11, color: V.ink4, marginTop: 2, fontFamily: V.sans }}>
                  {doc.project_name || 'No project'} · {doc.doc_date ? new Date(doc.doc_date).toLocaleDateString('en-GB') : 'No date'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                <DocTypeBadge type={doc.doc_type} />
                {doc.total_value && <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink2 }}>{formatValue(doc.total_value, doc.currency)}</span>}
                <span style={{
                  fontSize: 9, fontFamily: V.mono, letterSpacing: 0.5, padding: '2px 6px', borderRadius: 3,
                  color: doc.extraction_status === 'complete' ? V.good : doc.extraction_status === 'failed' ? V.bad : V.ink4,
                  background: doc.extraction_status === 'complete' ? V.goodSoft : doc.extraction_status === 'failed' ? V.badSoft : V.paper3,
                }}>
                  {doc.extraction_status === 'complete' ? 'EXTRACTED' : doc.extraction_status === 'failed' ? 'FAILED' : 'PENDING'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TAB: RATINGS ─────────────────────────────────────────────────────────────

function RatingsTab({ supplierId, onRateClick }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/suppliers/${supplierId}/ratings`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [supplierId]);

  if (loading) return <div style={{ fontSize: 13, color: V.ink4, padding: 24 }}>Loading ratings…</div>;
  if (!data)   return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {data.breakdown && data.breakdown.length > 0 && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={s.cardTitle}>Rating Breakdown</div>
            <button style={s.btnGold} onClick={onRateClick}>+ Rate supplier</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.breakdown.map(b => (
              <div key={b.category} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 120, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: V.ink3, fontFamily: V.sans }}>{b.category}</div>
                <div style={{ flex: 1, height: 4, background: V.paper3, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(b.avg / 5) * 100}%`, background: V.gold, borderRadius: 2 }}></div>
                </div>
                <div style={{ fontFamily: V.mono, fontSize: 12, color: V.ink2, width: 32, textAlign: 'right' }}>{Number(b.avg).toFixed(1)}</div>
                <div style={{ fontSize: 11, color: V.ink4 }}>({b.count})</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.ratings.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '40px 24px', fontSize: 13, color: V.ink4, fontFamily: V.sans }}>
          No ratings yet.
          <button style={s.btnGold} onClick={onRateClick}>Rate this supplier</button>
        </div>
      ) : (
        <div style={s.card}>
          <div style={s.cardTitle}>Rating History</div>
          {data.ratings.map((r, i) => (
            <div key={r.id} style={{ paddingTop: 12, marginTop: i > 0 ? 12 : 4, borderTop: i > 0 ? `0.5px solid ${V.line}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <StarRating value={r.rating} size={13} />
                  <span style={{ fontSize: 10, fontFamily: V.mono, letterSpacing: 1, color: V.ink4, textTransform: 'uppercase' }}>{r.category}</span>
                </div>
                <span style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>{new Date(r.rated_at).toLocaleDateString('en-GB')}</span>
              </div>
              {r.project_name && <div style={{ fontSize: 11, color: V.ink3, fontFamily: V.sans }}>Project: {r.project_name}</div>}
              {r.comment && <div style={{ fontSize: 12, color: V.ink2, marginTop: 4, fontFamily: V.sans, fontStyle: 'italic' }}>"{r.comment}"</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TAB: LINE ITEMS ─────────────────────────────────────────────────────────

function PriceTrendChart({ dataPoints, stats }) {
  if (!dataPoints || dataPoints.length < 2) return null;

  const prices = dataPoints.map(d => d.unit_price);
  const minP   = Math.min(...prices);
  const maxP   = Math.max(...prices);
  const range  = maxP - minP || 1;

  const W = 560, H = 120, PAD = 28;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;

  const pts = dataPoints.map((d, i) => ({
    x: PAD + (i / (dataPoints.length - 1)) * innerW,
    y: PAD + (1 - (d.unit_price - minP) / range) * innerH,
    price: d.unit_price,
    date:  d.doc_date ? d.doc_date.substring(0, 10) : '',
  }));

  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const areaPath = [
    `M ${pts[0].x},${PAD + innerH}`,
    ...pts.map(p => `L ${p.x},${p.y}`),
    `L ${pts[pts.length - 1].x},${PAD + innerH}`,
    'Z'
  ].join(' ');

  const trendColor = stats.trend === 'up' ? V.bad : stats.trend === 'down' ? V.good : V.gold;

  return (
    <div style={{ background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4, fontFamily: V.sans }}>Price Trend</div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'MIN',    val: stats.minPrice },
            { label: 'AVG',    val: stats.avgPrice },
            { label: 'MAX',    val: stats.maxPrice },
            { label: 'LATEST', val: stats.latestPrice },
          ].map(st => (
            <div key={st.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, letterSpacing: 1.5, color: V.ink4, fontFamily: V.sans }}>{st.label}</div>
              <div style={{ fontFamily: V.mono, fontSize: 12, color: V.ink, fontWeight: 500 }}>
                {stats.currency} {st.val.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={PAD} y1={PAD + t * innerH} x2={PAD + innerW} y2={PAD + t * innerH} stroke={V.line} strokeWidth={0.5} />
        ))}
        <path d={areaPath} fill={trendColor} fillOpacity={0.07} />
        <polyline points={polyline} fill="none" stroke={trendColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3} fill={trendColor} />)}
        <text x={PAD} y={H - 4} fontSize={9} fill={V.ink4} fontFamily="sans-serif">{pts[0].date}</text>
        <text x={PAD + innerW} y={H - 4} fontSize={9} fill={V.ink4} fontFamily="sans-serif" textAnchor="end">{pts[pts.length - 1].date}</text>
      </svg>
    </div>
  );
}

function LineItemsTab({ supplierId }) {
  const [lineItems, setLineItems]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [year, setYear]                 = useState('');
  const [years, setYears]               = useState([]);
  const [trendData, setTrendData]       = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendKeyword, setTrendKeyword] = useState('');

  const load = (s = search, y = year) => {
    setLoading(true);
    const params = new URLSearchParams({ supplier_id: supplierId });
    if (s) params.set('search', s);
    if (y) params.set('year', y);
    api.get(`/api/line-items?${params}`)
      .then(r => { setLineItems(r.data.lineItems || []); setYears(r.data.filters?.years || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [supplierId]);

  const loadTrend = async (keyword) => {
    if (!keyword || keyword.length < 3) return;
    setTrendLoading(true);
    setTrendKeyword(keyword);
    try {
      const r = await api.get(`/api/line-items/trends?description=${encodeURIComponent(keyword)}&supplier_id=${supplierId}`);
      setTrendData(r.data);
    } catch (e) { console.error(e); }
    finally { setTrendLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...s.input, width: 240 }}
          placeholder="Search item descriptions…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load(search, year)}
        />
        <select style={s.filterSelect} value={year} onChange={e => { setYear(e.target.value); load(search, e.target.value); }}>
          <option value="">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button style={s.btnGold} onClick={() => load(search, year)}>Search</button>
        {(search || year) && (
          <button style={s.btnGhost} onClick={() => { setSearch(''); setYear(''); load('', ''); }}>Clear</button>
        )}
      </div>

      {trendData && trendData.dataPoints?.length >= 2 && (
        <div>
          <div style={{ fontSize: 11, color: V.ink4, marginBottom: 8, fontFamily: V.sans }}>
            Price trend for: <strong style={{ color: V.ink }}>{trendKeyword}</strong>
            <button style={{ ...s.btnGhost, padding: '2px 8px', fontSize: 10, marginLeft: 10 }} onClick={() => setTrendData(null)}>✕</button>
          </div>
          <PriceTrendChart dataPoints={trendData.dataPoints} stats={trendData.stats} />
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: V.ink4, padding: 24, fontFamily: V.sans }}>Loading line items…</div>
      ) : lineItems.length === 0 ? (
        <div style={{ fontSize: 13, color: V.ink4, padding: '24px 0', fontFamily: V.sans }}>
          {search ? `No line items matching "${search}".` : 'No extracted line items yet.'}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>
            {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''} · Click a row to view price trend
          </div>
          <div style={{ border: `0.5px solid ${V.line}`, borderRadius: 6, background: V.paper, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: V.paper2 }}>
                  {['Description', 'Qty', 'Unit', 'Unit Price', 'Total', 'Currency', 'Project', 'Date'].map(h => (
                    <th key={h} style={{ fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4, padding: '10px 14px', textAlign: 'left', borderBottom: `1px solid ${V.line}`, fontFamily: V.sans, whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => (
                  <tr
                    key={item.id}
                    style={{ borderBottom: `0.5px solid ${V.line}`, cursor: 'pointer', transition: 'background 0.1s' }}
                    onClick={() => loadTrend(item.description)}
                    onMouseEnter={e => e.currentTarget.style.background = V.paper2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    title="Click to view price trend for this item"
                  >
                    <td style={{ padding: '12px 14px', fontFamily: V.sans, fontSize: 12, color: V.ink, maxWidth: 280 }}>
                      <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{item.description}</div>
                      {item.category_tag && <div style={{ fontSize: 9, fontFamily: V.mono, letterSpacing: 1, color: V.gold, textTransform: 'uppercase', marginTop: 2 }}>{item.category_tag}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 12, color: V.ink2 }}>{item.quantity ?? '—'}</td>
                    <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 11, color: V.ink3 }}>{item.unit || '—'}</td>
                    <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 13, color: V.ink, fontWeight: 500 }}>{item.unit_price != null ? item.unit_price.toLocaleString() : '—'}</td>
                    <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 12, color: V.ink2 }}>{item.total_price != null ? item.total_price.toLocaleString() : '—'}</td>
                    <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 11, color: V.ink3 }}>{item.currency || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: V.ink3, fontFamily: V.sans }}>{item.project_name || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: V.ink4, fontFamily: V.mono, whiteSpace: 'nowrap' }}>{item.doc_date ? item.doc_date.substring(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── SUPPLIER UPLOAD MODAL ────────────────────────────────────────────────────
// Stripped upload modal for offers filed directly on a supplier profile.
// Supplier is pre-filled and locked. Project is optional — can be linked later.

function SupplierUploadModal({ supplier, onClose, onSuccess }) {
  const [file, setFile]         = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState(null);
  const [form, setForm] = useState({
    document_type: 'Quotation', document_date: '',
    total_value: '', currency: 'EUR', notes: '',
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileChange = async (f) => {
    setFile(f); setError(null); setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res  = await api.post('/api/documents/extract-metadata', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const meta = res.data.metadata || {};
      const validTypes = ['Quotation','Proposal','Contract','Amendment','BOQ','Purchase Order','Variation Order','Invoice'];
      if (meta.document_type && validTypes.includes(meta.document_type)) setF('document_type', meta.document_type);
      if (meta.document_date) setF('document_date', meta.document_date);
      if (meta.total_value)   setF('total_value', String(meta.total_value));
      if (meta.currency && ['EUR','USD','GBP','AED'].includes(meta.currency)) setF('currency', meta.currency);
    } catch (e) { console.error('Extraction failed:', e); }
    finally { setExtracting(false); }
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a file.'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('supplier_id', supplier.id);
      fd.append('supplier', supplier.name);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      // No project_id — standalone offer
      await api.post('/api/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally { setUploading(false); }
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...s.modal, maxWidth: 500 }}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div>
            <div style={s.modalTitle}>Upload offer</div>
            <div style={s.modalSub}>Filing against <strong>{supplier.name}</strong> — no project required</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {/* File */}
          <div style={s.field}>
            <label style={s.fieldLabel}>File <span style={{ color: V.gold }}>*</span></label>
            <div
              style={{ ...s.dropZone, borderColor: file ? V.gold : V.line, background: file ? V.goldSoft : V.paper }}
              onClick={() => document.getElementById('sup-upload-input').click()}
            >
              <input id="sup-upload-input" type="file" accept=".pdf,.docx,.doc,.xlsx,.xls" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleFileChange(e.target.files[0])} />
              {file ? (
                <div style={{ fontSize: 13, color: V.ink2, fontFamily: V.sans }}>
                  <strong>{file.name}</strong>
                  {extracting && <span style={{ color: V.gold, marginLeft: 10 }}>✦ AI reading…</span>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>Click to select file — PDF, Word, Excel</div>
              )}
            </div>
          </div>

          {/* Supplier — locked */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Supplier</label>
            <div style={{ ...s.input, background: V.goodSoft, borderColor: '#b8d8c4', color: V.good, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13 }}>✓</span> {supplier.name}
            </div>
          </div>

          {/* Doc type + date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Document type</label>
              <select style={s.input} value={form.document_type} onChange={e => setF('document_type', e.target.value)}>
                {['Quotation','Proposal','Contract','Amendment','BOQ','Purchase Order','Variation Order','Invoice'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Document date</label>
              <input style={s.input} type="date" value={form.document_date} onChange={e => setF('document_date', e.target.value)} />
            </div>
          </div>

          {/* Value */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Total value</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <select style={{ ...s.input, width: 80, flexShrink: 0 }} value={form.currency} onChange={e => setF('currency', e.target.value)}>
                {['EUR','USD','GBP','AED'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input style={s.input} type="number" placeholder="0.00" value={form.total_value} onChange={e => setF('total_value', e.target.value)} />
            </div>
          </div>

          <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans, background: V.paper2, border: `0.5px solid ${V.line}`, borderRadius: 4, padding: '8px 12px' }}>
            This offer will be saved to {supplier.name}'s history without a project. You can link it to a project later from the Offer History tab.
          </div>

          {error && <div style={s.errorBox}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose} disabled={uploading}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: (uploading || extracting) ? 0.6 : 1 }} onClick={handleUpload} disabled={uploading || extracting}>
            {uploading ? 'Uploading…' : extracting ? 'Reading…' : 'Upload offer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LINK TO PROJECT MODAL ────────────────────────────────────────────────────
// Shows a dropdown of the client's projects and links a document to one.

function LinkToProjectModal({ documentId, documentName, onClose, onLinked }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [linking, setLinking]   = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    api.get('/api/projects').then(r => setProjects(r.data.projects || [])).catch(console.error);
  }, []);

  const handleLink = async () => {
    if (!projectId) { setError('Please select a project.'); return; }
    setLinking(true); setError(null);
    try {
      await api.post(`/api/documents/${documentId}/link-project`, { project_id: projectId });
      onLinked();
    } catch (err) {
      setError(err.response?.data?.error || 'Link failed.');
    } finally { setLinking(false); }
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...s.modal, maxWidth: 420 }}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div>
            <div style={s.modalTitle}>Link to project</div>
            <div style={s.modalSub} title={documentName}>{documentName}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.field}>
            <label style={s.fieldLabel}>Select project</label>
            <select style={s.input} value={projectId} onChange={e => setProjectId(e.target.value)}>
              <option value="">— Choose a project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>
            The document will appear in the selected project's Evidence Locker and will be available for comparison and reports.
          </div>
          {error && <div style={s.errorBox}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose} disabled={linking}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: linking ? 0.6 : 1 }} onClick={handleLink} disabled={linking}>
            {linking ? 'Linking…' : 'Link to project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TAB: OFFER HISTORY ───────────────────────────────────────────────────────

function ComparisonBadge({ offer }) {
  if (!offer.comparison_id) {
    return (
      <span style={{ fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 0.8, padding: '3px 8px', borderRadius: 3, textTransform: 'uppercase', background: V.paper2, color: V.ink4, border: `0.5px solid ${V.line}`, whiteSpace: 'nowrap' }}>
        Not compared
      </span>
    );
  }
  if (offer.is_best) {
    return (
      <span style={{ fontFamily: V.mono, fontSize: 9, fontWeight: 600, letterSpacing: 0.8, padding: '3px 8px', borderRadius: 3, textTransform: 'uppercase', background: V.goldSoft, color: V.warn, border: `0.5px solid #e8d08a`, whiteSpace: 'nowrap' }}>
        ★ Lowest · {offer.peer_count} suppliers
      </span>
    );
  }
  return (
    <span style={{ fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 0.8, padding: '3px 8px', borderRadius: 3, textTransform: 'uppercase', background: V.goodSoft, color: V.good, whiteSpace: 'nowrap' }}>
      Compared · {offer.peer_count} suppliers
    </span>
  );
}

function OfferHistoryTab({ supplierId, supplier }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null); // { id, name }

  // Active filters
  const [year, setYear]           = useState('');
  const [projectId, setProjectId] = useState('');
  const [docType, setDocType]     = useState('');
  const [commodity, setCommodity] = useState('');
  const [inputVal, setInputVal]   = useState('');
  const inputRef                  = useRef(null);

  const load = (y, pid, dt, cm) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (y)   params.set('year', y);
    if (pid) params.set('project_id', pid);
    if (dt)  params.set('doc_type', dt);
    if (cm)  params.set('commodity', cm);
    api.get(`/api/suppliers/${supplierId}/offer-history?${params}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load('', '', '', ''); }, [supplierId]);

  const handleSearch = () => {
    setCommodity(inputVal);
    load(year, projectId, docType, inputVal);
  };

  const handleClear = () => {
    setYear(''); setProjectId(''); setDocType('');
    setCommodity(''); setInputVal('');
    load('', '', '', '');
  };

  const hasFilters = year || projectId || docType || commodity;

  if (loading && !data) {
    return <div style={{ fontSize: 13, color: V.ink4, padding: 24, fontFamily: V.sans }}>Loading offer history…</div>;
  }

  const stats   = data?.stats   || {};
  const filters = data?.filters || {};
  const offers  = data?.offers  || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header row with Upload offer button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: V.ink3, fontFamily: V.sans }}>
          All offers and documents filed against this supplier
        </div>
        <button style={s.btnGold} onClick={() => setUploadOpen(true)}>
          ↑ Upload offer
        </button>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: V.line, border: `1px solid ${V.line}`, borderRadius: 4, overflow: 'hidden' }}>
        {[
          { label: 'TOTAL OFFERS',   value: stats.total_offers     || 0, sub: null },
          { label: 'COMPARED',       value: stats.compared_count   || 0, sub: `${(stats.total_offers || 0) - (stats.compared_count || 0)} standalone` },
          { label: 'PROJECTS',       value: stats.distinct_projects || 0, sub: null },
          {
            label: 'DATE RANGE',
            value: stats.date_min ? stats.date_min.substring(0, 4) : '—',
            sub:   stats.date_max && stats.date_max !== stats.date_min ? `through ${stats.date_max.substring(0, 4)}` : null,
          },
        ].map((k, i) => (
          <div key={i} style={{ background: V.paper, padding: '16px 20px', borderTop: `2px solid ${V.line}` }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginBottom: 6, textTransform: 'uppercase', fontFamily: V.sans }}>{k.label}</div>
            <div style={{ fontFamily: V.mono, fontSize: 26, fontWeight: 500, color: V.ink, lineHeight: 1 }}>{k.value}</div>
            {k.sub && <div style={{ fontSize: 10, color: V.ink4, marginTop: 4, fontFamily: V.sans }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          style={s.filterSelect}
          value={year}
          onChange={e => { setYear(e.target.value); load(e.target.value, projectId, docType, commodity); }}
        >
          <option value="">All years</option>
          {(filters.years || []).map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select
          style={s.filterSelect}
          value={projectId}
          onChange={e => { setProjectId(e.target.value); load(year, e.target.value, docType, commodity); }}
        >
          <option value="">All projects</option>
          {(filters.projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <select
          style={s.filterSelect}
          value={docType}
          onChange={e => { setDocType(e.target.value); load(year, projectId, e.target.value, commodity); }}
        >
          <option value="">All types</option>
          {(filters.docTypes || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <input
          ref={inputRef}
          style={{ ...s.input, width: 260, fontSize: 12, padding: '7px 12px' }}
          placeholder="Search product, commodity, filename…"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
        />
        <button style={s.btnGold} onClick={handleSearch}>Search</button>

        {hasFilters && (
          <button style={s.btnGhost} onClick={handleClear}>Clear</button>
        )}

        {loading && <span style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>Loading…</span>}
      </div>

      {/* Results count */}
      {!loading && (
        <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>
          {offers.length} offer{offers.length !== 1 ? 's' : ''}
          {commodity && ` matching "${commodity}"`}
        </div>
      )}

      {/* Table */}
      {offers.length === 0 && !loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 13, color: V.ink4, fontFamily: V.sans }}>
          {hasFilters
            ? 'No offers match the current filters.'
            : 'No offers on file for this supplier yet. Upload documents linked to this supplier to build their history.'
          }
        </div>
      ) : (
        <div style={{ border: `0.5px solid ${V.line}`, borderRadius: 6, background: V.paper, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: V.paper2 }}>
                {[
                  { label: 'Date',       w: 100 },
                  { label: 'Project',    w: null },
                  { label: 'Document',   w: 200 },
                  { label: 'Type',       w: 100 },
                  { label: 'Product / Commodity', w: 180 },
                  { label: 'Value',      w: 120, right: true },
                  { label: 'Cur',        w: 48  },
                  { label: 'Comparison', w: 170 },
                ].map(h => (
                  <th
                    key={h.label}
                    style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
                      color: V.ink4, padding: '10px 14px',
                      textAlign: h.right ? 'right' : 'left',
                      borderBottom: `1px solid ${V.line}`,
                      fontFamily: V.sans, whiteSpace: 'nowrap',
                      width: h.w || undefined,
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {offers.map((offer, i) => (
                <tr
                  key={offer.id}
                  style={{ borderBottom: `0.5px solid ${V.line}`, transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = V.paper2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Date */}
                  <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 11, color: V.ink4, whiteSpace: 'nowrap' }}>
                    {offer.document_date ? offer.document_date.substring(0, 10) : '—'}
                  </td>
                  {/* Project */}
                  <td style={{ padding: '12px 14px', fontSize: 12, color: V.ink, fontWeight: 500, fontFamily: V.sans }}>
                    {offer.project_name
                      ? offer.project_name
                      : (
                        <button
                          style={{ background: 'none', border: `1px dashed ${V.line}`, borderRadius: 3, padding: '3px 9px', fontSize: 11, color: V.ink4, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap' }}
                          onClick={() => setLinkTarget({ id: offer.id, name: offer.original_name })}
                        >
                          + Link to project
                        </button>
                      )
                    }
                  </td>
                  {/* Document name */}
                  <td style={{ padding: '12px 14px', maxWidth: 200 }}>
                    <div style={{ fontFamily: V.mono, fontSize: 11, color: V.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190 }} title={offer.original_name}>
                      {offer.original_name}
                    </div>
                  </td>
                  {/* Type */}
                  <td style={{ padding: '12px 14px' }}>
                    <DocTypeBadge type={offer.document_type} />
                  </td>
                  {/* Product */}
                  <td style={{ padding: '12px 14px', maxWidth: 180 }}>
                    {offer.product
                      ? <span style={{ fontFamily: V.mono, fontSize: 11, color: V.ink2 }}>{offer.product}</span>
                      : <span style={{ fontSize: 11, color: V.ink4 }}>—</span>
                    }
                  </td>
                  {/* Value */}
                  <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 12, fontWeight: 600, color: V.ink, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {offer.total_value != null ? Number(offer.total_value).toLocaleString('en-GB') : '—'}
                  </td>
                  {/* Currency */}
                  <td style={{ padding: '12px 14px', fontFamily: V.mono, fontSize: 10, color: V.ink4 }}>
                    {offer.currency || '—'}
                  </td>
                  {/* Comparison badge */}
                  <td style={{ padding: '12px 14px' }}>
                    <ComparisonBadge offer={offer} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {uploadOpen && supplier && (
        <SupplierUploadModal
          supplier={supplier}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setUploadOpen(false); load(year, projectId, docType, commodity); }}
        />
      )}
      {linkTarget && (
        <LinkToProjectModal
          documentId={linkTarget.id}
          documentName={linkTarget.name}
          onClose={() => setLinkTarget(null)}
          onLinked={() => { setLinkTarget(null); load(year, projectId, docType, commodity); }}
        />
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const TABS = ['Overview', 'Documents', 'Line Items', 'Ratings', 'Offer History'];

export default function SupplierProfile() {
  const { id }            = useParams();
  const navigate          = useNavigate();
  const { user, logout }  = useAuth();

  const [supplier, setSupplier]     = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState(0);
  const [rateOpen, setRateOpen]     = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const API   = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('clarix_token') || '';

  const load = () => {
    setLoading(true);
    api.get(`/api/suppliers/${id}`)
      .then(res => {
        setSupplier(res.data.supplier);
        setRecentDocs(res.data.recentDocuments || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: V.ink4, fontFamily: V.sans }}>
      Loading supplier profile…
    </div>
  );

  if (!supplier) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: V.ink4, fontFamily: V.sans }}>
      Supplier not found.
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink }}>
      <FontLoader />

      {/* Util bar */}
      <div style={{ background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: V.gold, flexShrink: 0 }}></div>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' }}>SUPPLIER PROFILE</span>
      </div>

      {/* Nav */}
      <div style={{ background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px' }}>
        <div style={{ fontFamily: V.serif, fontSize: 24, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <span style={{ color: V.gold }}>V</span><span style={{ color: V.ink }}>erdict</span>
        </div>
        <div style={{ width: 1, height: 18, background: V.line, margin: '0 12px' }}></div>
        <div style={{ display: 'flex', gap: 4, fontSize: 13, color: V.ink4 }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>Negotiations</span>
          <span style={{ padding: '0 6px' }}>/</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/suppliers')}>Suppliers</span>
          <span style={{ padding: '0 6px' }}>/</span>
          <span style={{ color: V.ink, fontWeight: 500 }}>{supplier.name}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
          {user && <span style={{ fontSize: 12, color: V.ink4 }}>{user.email}</span>}
          {logout && <button style={{ background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer', fontFamily: V.sans }} onClick={logout}>Sign out</button>}
        </div>
      </div>

      {/* Profile header */}
      <div style={{ background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '24px 48px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <CategoryBadge category={supplier.category} />
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 3, fontFamily: V.mono, letterSpacing: 0.5, color: supplier.is_active ? V.good : V.ink4, background: supplier.is_active ? V.goodSoft : V.paper3 }}>
                {supplier.is_active ? 'ACTIVE' : 'ARCHIVED'}
              </span>
            </div>
            <div style={{ fontFamily: V.serif, fontSize: 30, fontWeight: 400, color: V.ink, letterSpacing: -0.5, marginBottom: 6 }}>
              {supplier.name}
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: V.ink4, fontFamily: V.sans }}>
              {supplier.country       && <span>📍 {supplier.country}</span>}
              {supplier.contact_email && <span>✉ {supplier.contact_email}</span>}
              {supplier.contact_phone && <span>📞 {supplier.contact_phone}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnGhost} onClick={() => setRateOpen(true)}>Rate supplier</button>
            <button style={s.btnGhost} onClick={() => navigate('/suppliers')}>← Back to registry</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', display: 'flex', gap: 0 }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              background: 'none', border: 'none',
              borderBottom: activeTab === i ? `2px solid ${V.gold}` : '2px solid transparent',
              padding: '14px 18px', fontSize: 13,
              fontWeight: activeTab === i ? 600 : 400,
              color: activeTab === i ? V.ink : V.ink4,
              cursor: 'pointer', fontFamily: V.sans,
              transition: 'all 0.15s', marginBottom: -1,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '32px 48px', maxWidth: 1200, margin: '0 auto' }}>
        {activeTab === 0 && <OverviewTab supplier={supplier} recentDocuments={recentDocs} onRate={() => setRateOpen(true)} onPreview={(docId, name) => setPreviewDoc({ id: docId, name })} />}
        {activeTab === 1 && <DocumentsTab supplierId={id} onPreview={(docId, name) => setPreviewDoc({ id: docId, name })} />}
        {activeTab === 2 && <LineItemsTab supplierId={id} />}
        {activeTab === 3 && <RatingsTab supplierId={id} onRateClick={() => setRateOpen(true)} />}
        {activeTab === 4 && <OfferHistoryTab supplierId={id} supplier={supplier} />}
      </div>

      {/* Rate modal */}
      {rateOpen && (
        <RateModal
          supplierId={id}
          onClose={() => setRateOpen(false)}
          onSaved={() => { setRateOpen(false); load(); }}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(12,24,48,0.6)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setPreviewDoc(null)}>
          <div style={{ background:V.paper, borderRadius:6, width:'82vw', height:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(12,24,48,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', padding:'10px 14px', borderBottom:`1px solid ${V.line}`, background:V.paper2, flexShrink:0 }}>
              <span style={{ fontSize:12, fontFamily:V.mono, color:V.ink3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:12 }}>{previewDoc.name}</span>
              <a href={`${API}/api/documents/${previewDoc.id}/file?token=${token}`} target="_blank" rel="noreferrer" style={{ fontSize:11, color:V.ink4, marginRight:16, textDecoration:'underline', textUnderlineOffset:2, whiteSpace:'nowrap', fontFamily:V.sans }}>Open in tab</a>
              <button onClick={() => setPreviewDoc(null)} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:V.ink3, padding:'0 2px', lineHeight:1 }}>✕</button>
            </div>
            <iframe src={`${API}/api/documents/${previewDoc.id}/file?token=${token}`} style={{ flex:1, border:'none', width:'100%' }} title={previewDoc.name} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────

const s = {
  card:        { background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 },
  cardTitle:   { fontFamily: V.serif, fontSize: 15, fontWeight: 500, color: V.ink },
  filterSelect:{ background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '8px 10px', fontSize: 12, color: V.ink, fontFamily: V.sans, outline: 'none', cursor: 'pointer' },

  field:       { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel:  { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3, fontFamily: V.sans },
  input:       { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorBox:    { background: '#f4e8e5', border: `1px solid ${V.bad}`, borderRadius: 4, padding: '10px 14px', fontSize: 12, color: V.bad },

  overlay:     { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:       { background: V.paper, borderRadius: 6, width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 64px rgba(12,24,48,0.22)' },
  modalHeader: { padding: '20px 24px 16px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle:  { fontFamily: V.serif, fontSize: 18, fontWeight: 500, color: V.ink },
  closeBtn:    { background: 'none', border: 'none', color: V.ink4, fontSize: 16, cursor: 'pointer', padding: 4 },
  modalBody:   { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, background: V.paper2 },
  modalFooter: { padding: '14px 24px', borderTop: `1px solid ${V.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: V.paper },

  btnGold:     { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans },
  btnGhost:    { background: 'transparent', color: V.ink, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 20px', fontSize: 13, cursor: 'pointer', fontFamily: V.sans },
};
